import React from 'react';
import { cn } from '../../lib/cn';
import { Breadcrumb } from '../Breadcrumb/Breadcrumb';
import { IconButton } from '../IconButton/IconButton';
import { UserMenu } from '../UserMenu/UserMenu';
import type { SidebarUser } from '../Sidebar/Sidebar';

export interface TopBarProps {
  title?: React.ReactNode;
  /** Workspace + page trail — prefer over `title` for standard pages. */
  breadcrumb?: string[];
  /** Email-style large breadcrumb with spectrum on the last segment. */
  titleGradient?: boolean;
  actions?: React.ReactNode;
  live?: boolean;
  onBell?: () => void;
  /** The account menu, pinned far-right of the bar (spec 41). */
  user?: SidebarUser;
  className?: string;
}

export function TopBar({
  title,
  breadcrumb,
  titleGradient,
  actions,
  live,
  onBell,
  user,
  className,
}: TopBarProps) {
  const left =
    title ??
    (breadcrumb && breadcrumb.length > 0 ? (
      <Breadcrumb items={breadcrumb} titleGradient={titleGradient ?? false} />
    ) : null);

  return (
    <header
      className={cn(
        'sticky top-0 z-[100] flex min-h-[var(--topbar-h)] shrink-0 items-center justify-between gap-3.5',
        'border-b border-[var(--cream-2)] px-[30px] py-3',
        'bg-[var(--surface-app)]',
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">{left}</div>
      <div className="flex shrink-0 items-center gap-2">
        {live ? (
          <span className="inline-flex h-8 items-center gap-1.5 rounded-[var(--r-pill)] bg-[var(--ai-tint)] px-[11px]">
            <span className="elva-live" />
            <span className="text-[11.5px] font-semibold text-[var(--ai-3)]">Live</span>
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">now</span>
          </span>
        ) : null}
        {onBell !== undefined ? (
          <IconButton icon="bell" size={32} onClick={onBell} label="Notifications" />
        ) : null}
        {actions}
        {user ? (
          <>
            {actions ? <span className="mx-0.5 h-6 w-px bg-[var(--cream-2)]" aria-hidden /> : null}
            <UserMenu user={user} />
          </>
        ) : null}
      </div>
    </header>
  );
}
